import { motion } from 'motion/react'
import { Instagram, Facebook, Music2 } from 'lucide-react'
import {
  CALENDLY_URL,
  INSTAGRAM_URL,
  TIKTOK_URL,
  FACEBOOK_URL,
} from './constants'
import { useLanguage } from '@/i18n/LanguageContext'

const socials = [
  { label: 'Instagram', href: INSTAGRAM_URL, icon: Instagram },
  { label: 'TikTok', href: TIKTOK_URL, icon: Music2 },
  { label: 'Facebook', href: FACEBOOK_URL, icon: Facebook },
]

export default function FinalCtaSection() {
  const { t } = useLanguage()

  return (
    <section id="contatti" className="relative bg-cream py-24 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-6xl px-6">
        <div className="relative grid items-center gap-14 md:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
            className="relative z-10 md:col-span-7"
          >
            <blockquote className="border-l-2 border-terracotta/50 pl-6 md:pl-8">
              <p className="font-display italic text-2xl md:text-3xl leading-snug text-ink">
                {t.finalCta.quote}
              </p>
              <cite className="mt-4 block text-sm uppercase tracking-[0.2em] text-ink/50 not-italic">
                Eugenio Montale
              </cite>
            </blockquote>

            <h2 className="mt-12 font-display text-4xl md:text-5xl leading-tight text-ink">
              {t.finalCta.titleBefore}{' '}
              <span className="italic text-terracotta">
                {t.finalCta.titleAccent}
              </span>
            </h2>

            <div className="mt-8 flex flex-col items-center md:inline-flex">
              <motion.a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-terracotta px-10 py-5 text-lg font-medium text-cream shadow-xl shadow-terracotta/30 hover:bg-terracotta/90"
              >
                {t.common.ctaLabel}
              </motion.a>
              <p className="mt-4 text-center text-sm text-ink/60">
                {t.finalCta.subtext}
              </p>
            </div>

            <div className="mt-10 hidden items-center gap-4 md:flex">
              {socials.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  whileHover={{ y: -3, scale: 1.08 }}
                  transition={{ type: 'spring', stiffness: 350, damping: 16 }}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink/70 hover:border-terracotta hover:text-terracotta"
                >
                  <social.icon className="h-5 w-5" strokeWidth={1.8} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 4 }}
            whileInView={{ opacity: 1, y: 0, rotate: 2 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="relative md:col-span-5"
          >
            <div className="relative mx-auto w-4/5 md:w-full">
              <div className="overflow-hidden rounded-t-full rounded-b-[2rem] shadow-[0_30px_60px_-25px_rgba(15,43,70,0.45)]">
                <img
                  src="/images/panorama.jpg"
                  alt={t.finalCta.positanoAlt}
                  className="aspect-[3/4] w-full object-cover"
                  loading="lazy"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, rotate: -6, x: -20 }}
                whileInView={{ opacity: 1, rotate: -4, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.35 }}
                whileHover={{ rotate: 1.5, transition: { type: 'spring', stiffness: 260, damping: 14, delay: 0 } }}
                className="absolute -bottom-12 -left-6 w-36 rounded-md bg-white p-2.5 pb-3 shadow-xl md:-left-16 md:w-52"
              >
                <img
                  src="/images/sottosopra.jpg"
                  alt={t.finalCta.polaroidCaption}
                  className="aspect-square w-full rounded-sm object-cover"
                  loading="lazy"
                />
                <p className="mt-2.5 text-center font-display text-sm italic text-ink/80 md:text-lg">
                  {t.finalCta.polaroidCaption}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
